import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { Input } from "@/components/ui/input";
import { adminApi, inr } from "@/lib/api";
import { Btn } from "@/admin/ui";

const todayIst = () => new Date(Date.now() + 19800000).toISOString().slice(0, 10);
const ROUTE_STATUSES = ["confirmed", "packed", "out_for_delivery"];
const PAY_SHORT = { cod: "Cash", upi_delivery: "UPI", bank_transfer: "Bank" };

export const collectAmount = (o) => (o.payment_method === "bank_transfer" ? 0 : o.amount_due);
const collectText = (o) => {
  if (o.payment_method !== "bank_transfer") return inr(o.amount_due);
  return o.payment_status === "verified" ? "PAID" : "NOT VERIFIED";
};

const groupByArea = (orders) => {
  const g = {};
  orders.forEach((o) => { (g[`${o.area_name} · ${o.pincode}`] ||= []).push(o); });
  return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
};

const Stop = ({ o, n }) => (
  <tr className="border-b border-stone-300 align-top" data-testid={`route-stop-${o.id}`}>
    <td className="py-2 pr-2 font-bold">{n}</td>
    <td className="pr-2"><p className="font-bold">{o.name}</p><p className="text-xs">{o.order_number} · {o.items.filter((i) => i.status !== "unavailable").length} items</p></td>
    <td className="pr-2 font-semibold whitespace-nowrap" data-testid={`route-phone-${o.id}`}>+91 {o.phone}</td>
    <td className="pr-2 text-xs">{o.address.line1}{o.address.landmark ? `, ${o.address.landmark}` : ""}{o.customer_note && <i className="block">Note: {o.customer_note}</i>}</td>
    <td className="pr-2 text-xs">{PAY_SHORT[o.payment_method]}</td>
    <td className="text-right font-extrabold whitespace-nowrap" data-testid={`route-collect-${o.id}`}>{collectText(o)}</td>
    <td className="pl-2 w-16"><span className="inline-block w-4 h-4 border border-black" /></td>
  </tr>
);

const Sheet = ({ slot, orders }) => {
  const total = orders.reduce((s, o) => s + collectAmount(o), 0);
  let n = 0;
  return (
    <article className="bg-white text-black border border-stone-300 rounded-md p-6 mb-6 break-after-page print:border-0 print:p-0 print:mb-0" data-testid="route-sheet">
      <header className="flex justify-between items-end border-b-2 border-black pb-2 mb-2">
        <div><p className="font-display text-2xl font-semibold leading-none">Rider route sheet</p><p className="text-sm font-bold mt-1" data-testid="route-sheet-slot">{slot}</p></div>
        <div className="text-right text-sm"><p>{orders.length} stops</p><p className="font-extrabold text-lg" data-testid="route-sheet-total">Collect {inr(total)}</p></div>
      </header>
      {groupByArea(orders).map(([area, list]) => (
        <section key={area} className="mb-3" data-testid={`route-area-${list[0].pincode}`}>
          <p className="bg-stone-100 [print-color-adjust:exact] px-2 py-1 text-sm font-bold flex justify-between"><span>{area}</span><span>{list.length} stops · {inr(list.reduce((s, o) => s + collectAmount(o), 0))}</span></p>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-wider"><th>#</th><th>Customer</th><th>Phone</th><th>Address</th><th>Pay</th><th className="text-right">Collect</th><th className="pl-2">Done</th></tr></thead>
            <tbody>{list.map((o) => <Stop key={o.id} o={o} n={++n} />)}</tbody>
          </table>
        </section>
      ))}
      <p className="text-xs mt-4">Rider name: ____________________ &nbsp; Cash handed over: ₹__________ &nbsp; Sign: __________</p>
    </article>
  );
};

export default function AdminRouteSheet() {
  const [params, setParams] = useSearchParams();
  const date = params.get("date") || todayIst();
  const slot = params.get("slot") || "all";
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    adminApi.get("/orders", { params: { date } }).then((r) => setOrders(r.data.filter((o) => ROUTE_STATUSES.includes(o.status))));
  }, [date]);

  const bySlot = useMemo(() => {
    const g = {};
    (orders || []).forEach((o) => { (g[o.slot_label] ||= []).push(o); });
    Object.values(g).forEach((l) => l.sort((a, b) => a.area_name.localeCompare(b.area_name) || a.order_number.localeCompare(b.order_number)));
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [orders]);
  const shown = slot === "all" ? bySlot : bySlot.filter(([s]) => s === slot);

  return (
    <div className="max-w-4xl">
      <div className="flex flex-wrap items-center gap-2 mb-4 print:hidden" data-testid="route-toolbar">
        <h1 className="font-display text-3xl text-primary font-semibold mr-auto">Rider route sheets</h1>
        <Input type="date" value={date} onChange={(e) => setParams({ date: e.target.value })} className="h-9 w-40 bg-white" data-testid="route-date-input" />
        <select value={slot} onChange={(e) => setParams({ date, slot: e.target.value })} className="h-9 rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="route-slot-select">
          <option value="all">All slots</option>
          {bySlot.map(([s]) => <option key={s} value={s}>{s}</option>)}
        </select>
        <Btn onClick={() => window.print()} disabled={!shown.length} data-testid="route-print-btn"><Printer className="w-4 h-4 inline mr-1" />Print {shown.length} sheet{shown.length === 1 ? "" : "s"}</Btn>
      </div>
      <p className="text-xs text-stone-500 mb-3 print:hidden">Includes confirmed, packed and out-for-delivery orders. Bank transfers show PAID or NOT VERIFIED — riders collect ₹0 on those.</p>
      {orders && !shown.length && <p className="text-sm text-stone-500 print:hidden" data-testid="route-empty">No orders ready for delivery on {date}.</p>}
      {shown.map(([s, list]) => <Sheet key={s} slot={`${date} · ${s}`} orders={list} />)}
    </div>
  );
}
